import { useEffect, useState } from "react";
import { formatEther, parseEther, isAddress, type Abi } from "viem";
import { useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { activeChain, COLOR_WHEEL_ADDRESS, explorerTx, shortAddress } from "../lib/chain";
import { colorWheelAbi } from "../lib/contracts";

/**
 * Owner-only control room. It renders ONLY when the connected wallet is the
 * contract owner (App gates it) and every action here is `onlyOwner` /
 * permissionless ON-CHAIN — the wallet key is the real login, so a non-owner
 * can neither see this panel nor execute anything in it.
 */
type Props = { onClose: () => void };

const abi = colorWheelAbi as Abi;
const read = (functionName: string, args?: unknown[]) => ({
  address: COLOR_WHEEL_ADDRESS,
  abi,
  functionName,
  ...(args ? { args } : {}),
});

export function AdminPanel({ onClose }: Props) {
  const { data, refetch } = useReadContracts({
    contracts: [
      read("treasuryBalance"),
      read("maxBet"),
      read("reservedPayout"),
      read("unclaimedPayout"),
      read("openStakes"),
      read("burnPerBet"),
      read("minBet"),
      read("getProvider"),
      read("feeTreasury"),
      read("wheelToken"),
      read("pendingWheelToken"),
      read("pendingWheelTokenTime"),
      read("maxPayoutBps"),
    ],
    query: { refetchInterval: 8000 },
  });
  const r = (data ?? []).map((d) => d?.result);
  const treasury = r[0] as bigint | undefined;
  const maxBet = r[1] as bigint | undefined;
  const reserved = r[2] as bigint | undefined;
  const unclaimed = r[3] as bigint | undefined;
  const open = r[4] as bigint | undefined;
  const burn = r[5] as bigint | undefined;
  const minBet = r[6] as bigint | undefined;
  const provider = r[7] as string | undefined;
  const feeTreasury = r[8] as string | undefined;
  const token = r[9] as string | undefined;
  const pendingTok = r[10] as string | undefined;
  const pendingTime = r[11] as bigint | undefined;
  const maxBps = r[12] as bigint | undefined;

  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();
  const { isLoading: mining, isSuccess } = useWaitForTransactionReceipt({ hash });
  const busy = isPending || mining;
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (isSuccess) {
      setNote("done ✓");
      void refetch();
    }
  }, [isSuccess, refetch]);
  useEffect(() => {
    if (error) setNote(error.message.split("\n")[0]);
  }, [error]);

  const run = (functionName: string, args: unknown[], value?: bigint) => {
    setNote(null);
    reset();
    try {
      writeContract({ address: COLOR_WHEEL_ADDRESS, abi, functionName, args, value, chainId: activeChain.id });
    } catch (e) {
      setNote(e instanceof Error ? e.message : "failed");
    }
  };

  // form state
  const [fundEth, setFundEth] = useState("");
  const [wdAmt, setWdAmt] = useState("");
  const [wdTo, setWdTo] = useState("");
  const [sweepAmt, setSweepAmt] = useState("");
  const [rid, setRid] = useState("");
  const [player, setPlayer] = useState("");
  const [burnWheel, setBurnWheel] = useState("");
  const [minEth, setMinEth] = useState("");
  const [payoutPct, setPayoutPct] = useState("");
  const [newFee, setNewFee] = useState("");
  const [newTok, setNewTok] = useState("");

  const eth = (v?: bigint) => (v === undefined ? "…" : `${Number(formatEther(v)).toFixed(4)} ETH`);
  const bigOk = (s: string) => {
    try {
      return parseEther(s) > 0n;
    } catch {
      return false;
    }
  };
  const ridOk = (() => {
    try {
      return BigInt(rid) >= 0n;
    } catch {
      return false;
    }
  })();

  // live lookup of the round being rescued
  const { data: roundData } = useReadContracts({
    contracts: ridOk
      ? [read("rounds", [BigInt(rid)]), read("isRefundable", [BigInt(rid)])]
      : [],
    query: { enabled: ridOk, refetchInterval: 5000 },
  });
  const round = roundData?.[0]?.result as
    | [bigint, bigint, bigint, bigint, number, number, boolean, boolean, string, string]
    | undefined;
  const refundable = roundData?.[1]?.result as boolean | undefined;

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-head">
          <h2>⚙️ ADMIN CONTROL ROOM</h2>
          <button className="ctl-btn" onClick={onClose}>
            CLOSE
          </button>
        </div>

        {note && (
          <div className="admin-note">
            {note}
            {hash && (
              <>
                {" · "}
                <a href={explorerTx(hash)} target="_blank" rel="noreferrer">
                  view tx
                </a>
              </>
            )}
          </div>
        )}

        <section className="admin-stats">
          <span>Treasury <b>{eth(treasury)}</b></span>
          <span>Max bet <b>{eth(maxBet)}</b></span>
          <span>Reserved <b>{eth(reserved)}</b></span>
          <span>Unclaimed <b>{eth(unclaimed)}</b></span>
          <span>Open stakes <b>{eth(open)}</b></span>
          <span>Burn/bet <b>{burn === undefined ? "…" : `${Number(formatEther(burn)).toLocaleString()} WHEEL`}</b></span>
          <span>Min bet <b>{eth(minBet)}</b></span>
          <span>Payout cap <b>{maxBps === undefined ? "…" : `${Number(maxBps) / 100}%`}</b></span>
          <span>VRF provider <b>{provider ? shortAddress(provider) : "…"}</b></span>
          <span>Fee treasury <b>{feeTreasury ? shortAddress(feeTreasury) : "…"}</b></span>
          <span>WHEEL token <b>{token ? shortAddress(token) : "…"}</b></span>
        </section>

        <section className="admin-block">
          <h3>TREASURY</h3>
          <div className="admin-row">
            <input placeholder="ETH to add" value={fundEth} onChange={(e) => setFundEth(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !bigOk(fundEth)}
              onClick={() => run("fundTreasury", [], parseEther(fundEth))}
            >
              FUND TREASURY
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="ETH amount" value={wdAmt} onChange={(e) => setWdAmt(e.target.value)} />
            <input placeholder="to address" value={wdTo} onChange={(e) => setWdTo(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !bigOk(wdAmt) || !isAddress(wdTo)}
              onClick={() => run("withdraw", [parseEther(wdAmt), wdTo])}
            >
              WITHDRAW
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="ETH to sweep → fee treasury" value={sweepAmt} onChange={(e) => setSweepAmt(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !bigOk(sweepAmt)}
              onClick={() => run("sweepToFeeTreasury", [parseEther(sweepAmt)])}
            >
              SWEEP
            </button>
          </div>
          <p className="admin-hint">Withdrawals can never touch reserved coverage (open stakes + potential payouts + unclaimed wins).</p>
        </section>

        <section className="admin-block">
          <h3>ROUND RESCUE · keeper-down fallback</h3>
          <div className="admin-row">
            <input placeholder="round #" value={rid} onChange={(e) => setRid(e.target.value)} />
            {round && (
              <span className="admin-round-state">
                {round[6] ? (round[7] ? "voided" : "settled") : round[2] > 0n ? "closed" : "open"}
                {" · "}staked {eth(round[0])}
                {refundable ? " · REFUNDABLE" : ""}
              </span>
            )}
          </div>
          <div className="admin-row">
            <button
              className="cta sm"
              disabled={busy || !ridOk}
              onClick={() => run("closeRound", [BigInt(rid)])}
            >
              CLOSE
            </button>
            <button
              className="cta sm"
              disabled={busy || !ridOk}
              onClick={() => run("voidRound", [BigInt(rid)])}
            >
              VOID (→refunds)
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="player address" value={player} onChange={(e) => setPlayer(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !ridOk || !isAddress(player)}
              onClick={() => run("payWinner", [BigInt(rid), player])}
            >
              PAY WINNER
            </button>
            <button
              className="cta sm"
              disabled={busy || !ridOk || !isAddress(player)}
              onClick={() => run("refundTo", [BigInt(rid), player])}
            >
              REFUND BACKER
            </button>
          </div>
          <p className="admin-hint">
            Settling needs the keeper's secret seed and can't be done here — but PAY WINNER / REFUND BACKER /
            VOID push funds to their rightful owners once a round is settled or voided. All permissionless &amp; safe.
          </p>
        </section>

        <section className="admin-block">
          <h3>SETTINGS</h3>
          <div className="admin-row">
            <input placeholder="burn per bet (WHEEL)" value={burnWheel} onChange={(e) => setBurnWheel(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !bigOk(burnWheel)}
              onClick={() => run("setBurnPerBet", [parseEther(burnWheel)])}
            >
              SET BURN
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="min bet (ETH)" value={minEth} onChange={(e) => setMinEth(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !bigOk(minEth)}
              onClick={() => run("setMinBet", [parseEther(minEth)])}
            >
              SET MIN BET
            </button>
          </div>
          <div className="admin-row">
            <input
              placeholder={`max payout % of treasury (now ${maxBps ? Number(maxBps) / 100 : "—"}%)`}
              value={payoutPct}
              onChange={(e) => setPayoutPct(e.target.value)}
            />
            <button
              className="cta sm"
              disabled={busy || !(Number(payoutPct) >= 5 && Number(payoutPct) <= 100)}
              onClick={() => run("setMaxPayoutBps", [BigInt(Math.round(Number(payoutPct) * 100))])}
            >
              SET PAYOUT CAP
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="fee treasury address" value={newFee} onChange={(e) => setNewFee(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !isAddress(newFee)}
              onClick={() => run("setFeeTreasury", [newFee])}
            >
              SET FEE TREASURY
            </button>
          </div>
          <div className="admin-row">
            <input placeholder="new WHEEL token address" value={newTok} onChange={(e) => setNewTok(e.target.value)} />
            <button
              className="cta sm"
              disabled={busy || !isAddress(newTok)}
              onClick={() => run("announceWheelToken", [newTok])}
            >
              ANNOUNCE TOKEN
            </button>
            <button
              className="cta sm"
              disabled={busy || !pendingTok || pendingTok === "0x0000000000000000000000000000000000000000"}
              onClick={() => run("applyWheelToken", [])}
            >
              APPLY
            </button>
          </div>
          {pendingTok && pendingTok !== "0x0000000000000000000000000000000000000000" && (
            <p className="admin-hint">
              Pending token {shortAddress(pendingTok)} — applies after{" "}
              {pendingTime ? new Date(Number(pendingTime) * 1000).toLocaleString() : "the timelock"}.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
